import React, { useEffect, useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Checkbox,
  FormControlLabel,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  useTheme,
  Tabs,
  Tab,
  Switch,
  FormGroup,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import SettingsIcon from '@mui/icons-material/Settings'
import FilterListIcon from '@mui/icons-material/FilterList'
import ViewModuleIcon from '@mui/icons-material/ViewModule'
import { makeGetRequest, makePostRequest, makePutRequest } from 'utils/api'

const DashboardSettingsModal = ({ open, onClose, onSave, currentLayout }) => {
  const theme = useTheme()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [categories, setCategories] = useState([])
  const [expandedCategories, setExpandedCategories] = useState([])
  const [activeTab, setActiveTab] = useState(0)
  const [settings, setSettings] = useState({
    excluded_categories: [],
    excluded_subcategories: [],
    layout_preferences: {
      hidden_cards: [],
      card_order: [],
    },
  })
  const [availableCards, setAvailableCards] = useState([])

  useEffect(() => {
    if (open) {
      loadData()
    }
  }, [open])

  const loadData = async () => {
    setLoading(true)
    try {
      // Load actual available cards from reports API
      const reportsResponse = await makeGetRequest('/api/Finance/reports/', {}, true)
      
      // Map API cards using actual API fields
      const cards = reportsResponse.data.map((report, index) => ({
        id: report.id,
        calcul: report.calcul,
        name: report.titre || report.nom,
        description: report.nom_graph || report.nom,
        order: index,
      }))
      
      setAvailableCards(cards)

      // Load categories and subcategories
      const categoriesResponse = await makeGetRequest('/api/Finance/Categoriesfi/', {}, true)
      
      // Group categories by main category
      const groupedCategories = {}
      categoriesResponse.data.forEach((cat) => {
        const mainCat = cat.Categorie
        if (!groupedCategories[mainCat]) {
          groupedCategories[mainCat] = {
            name: mainCat,
            id: mainCat,
            subcategories: [],
          }
        }
        groupedCategories[mainCat].subcategories.push({
          id: cat.id,
          name: cat.SousCategorie,
          mouvement: cat.Mouvement,
        })
      })

      const categoriesArray = Object.values(groupedCategories)
      setCategories(categoriesArray)

      // Expand all categories by default
      setExpandedCategories(categoriesArray.map((cat) => cat.id))

      // Load existing settings
      const settingsResponse = await makeGetRequest('/api/Finance/dashboard-settings/', {}, true)
      if (settingsResponse.data) {
        const layoutPrefs = settingsResponse.data.layout_preferences || {}
        
        setSettings({
          excluded_categories: settingsResponse.data.excluded_categories || [],
          excluded_subcategories: settingsResponse.data.excluded_subcategories || [],
          layout_preferences: {
            hidden_cards: layoutPrefs.hidden_cards || [],
            card_order: layoutPrefs.card_order || [],
          },
        })
      }
    } catch (error) {
      console.error('Error loading dashboard settings:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleCategoryToggle = (categoryId) => {
    const excluded = [...settings.excluded_categories]
    const index = excluded.indexOf(categoryId)

    if (index > -1) {
      excluded.splice(index, 1)
    } else {
      excluded.push(categoryId)
    }

    setSettings({ ...settings, excluded_categories: excluded })
  }

  const handleSubcategoryToggle = (subcategoryId) => {
    const excluded = [...settings.excluded_subcategories]
    const index = excluded.indexOf(subcategoryId)

    if (index > -1) {
      excluded.splice(index, 1)
    } else {
      excluded.push(subcategoryId)
    }

    setSettings({ ...settings, excluded_subcategories: excluded })
  }

  const isCategoryExcluded = (categoryId) => {
    return settings.excluded_categories.includes(categoryId)
  }

  const isSubcategoryExcluded = (subcategoryId) => {
    return settings.excluded_subcategories.includes(subcategoryId)
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      await makePutRequest('/api/Finance/dashboard-settings/0/', settings, {}, true)
      if (onSave) {
        onSave(settings)
      }
      onClose()
    } catch (error) {
      console.error('Error saving dashboard settings:', error)
      alert('Failed to save settings. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const handleSelectAll = () => {
    setSettings({
      ...settings,
      excluded_categories: [],
      excluded_subcategories: [],
    })
  }

  const handleDeselectAll = () => {
    const allCategories = categories.map((cat) => cat.id)
    const allSubcategories = categories.flatMap((cat) =>
      cat.subcategories.map((sub) => sub.id)
    )
    setSettings({
      ...settings,
      excluded_categories: allCategories,
      excluded_subcategories: allSubcategories,
    })
  }

  const handleShowAllCards = () => {
    setSettings({
      ...settings,
      layout_preferences: {
        ...settings.layout_preferences,
        hidden_cards: [],
      },
    })
  }

  const handleHideAllCards = () => {
    const allCardIds = availableCards.map((card) => card.id)
    setSettings({
      ...settings,
      layout_preferences: {
        ...settings.layout_preferences,
        hidden_cards: allCardIds,
      },
    })
  }

  const handleAccordionChange = (categoryId) => (event, isExpanded) => {
    setExpandedCategories((prev) =>
      isExpanded ? [...prev, categoryId] : prev.filter((id) => id !== categoryId)
    )
  }

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue)
  }

  const handleCardVisibilityToggle = (cardId) => {
    const hiddenCards = [...settings.layout_preferences.hidden_cards]
    const index = hiddenCards.indexOf(cardId)
    
    if (index > -1) {
      hiddenCards.splice(index, 1)
    } else {
      hiddenCards.push(cardId)
    }
    
    setSettings({
      ...settings,
      layout_preferences: {
        ...settings.layout_preferences,
        hidden_cards: hiddenCards,
      },
    })
  }

  const isCardVisible = (cardId) => {
    return !settings.layout_preferences.hidden_cards.includes(cardId)
  }


  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <SettingsIcon />
          <Typography variant="h6">Paramètres du Tableau de Bord</Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="fullWidth"
              sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
            >
              <Tab
                icon={<FilterListIcon />}
                label="Filtre de Contenu"
                iconPosition="start"
              />
              <Tab
                icon={<ViewModuleIcon />}
                label="Disposition"
                iconPosition="start"
              />
            </Tabs>

            {/* Tab 1: Content Filter */}
            {activeTab === 0 && (
              <Box>
                <Box mb={2}>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Sélectionnez les catégories et sous-catégories à afficher sur le tableau de bord. 
                    Les éléments non cochés seront exclus des cartes, graphiques et totaux.
                  </Typography>
                </Box>

                <Box mb={2} display="flex" gap={2}>
                  <Button size="small" variant="outlined" onClick={handleSelectAll}>
                    Tout Sélectionner
                  </Button>
                  <Button size="small" variant="outlined" onClick={handleDeselectAll}>
                    Tout Désélectionner
                  </Button>
                </Box>

                {categories.map((category) => (
              <Accordion
                key={category.id}
                expanded={expandedCategories.includes(category.id)}
                onChange={handleAccordionChange(category.id)}
                sx={{
                  backgroundColor: theme.palette.background.paper,
                  mb: 1,
                  border: '1px solid',
                  borderColor: theme.palette.divider,
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!isCategoryExcluded(category.id)}
                        onChange={(e) => {
                          e.stopPropagation()
                          handleCategoryToggle(category.id)
                        }}
                        onClick={(e) => e.stopPropagation()}
                      />
                    }
                    label={
                      <Typography variant="subtitle1" fontWeight="bold">
                        {category.name}
                      </Typography>
                    }
                    onClick={(e) => e.stopPropagation()}
                  />
                </AccordionSummary>
                <AccordionDetails>
                  <Box pl={4}>
                    {category.subcategories.map((subcategory) => (
                      <Box key={subcategory.id} mb={0.5}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={!isSubcategoryExcluded(subcategory.id)}
                              onChange={() => handleSubcategoryToggle(subcategory.id)}
                            />
                          }
                          label={
                            <Typography variant="body2">
                              {subcategory.name}
                              {subcategory.mouvement && (
                                <Typography
                                  component="span"
                                  variant="caption"
                                  color="textSecondary"
                                  ml={1}
                                >
                                  ({subcategory.mouvement})
                                </Typography>
                              )}
                            </Typography>
                          }
                        />
                      </Box>
                    ))}
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))}
              </Box>
            )}

            {/* Tab 2: Layout */}
            {activeTab === 1 && (
              <Box>
                <Box mb={2}>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Afficher ou masquer les cartes du tableau de bord. L'ordre des cartes peut être modifié en les faisant glisser sur le tableau de bord.
                  </Typography>
                </Box>

                <Box mb={2} display="flex" gap={2}>
                  <Button size="small" variant="outlined" onClick={handleShowAllCards}>
                    Tout Afficher
                  </Button>
                  <Button size="small" variant="outlined" onClick={handleHideAllCards}>
                    Tout Masquer
                  </Button>
                </Box>

                <FormGroup>
                  {availableCards.map((card) => (
                    <Box
                      key={card.id}
                      sx={{
                        p: 2,
                        mb: 1,
                        border: '1px solid',
                        borderColor: theme.palette.divider,
                        borderRadius: 1,
                        backgroundColor: theme.palette.background.paper,
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Switch
                            checked={isCardVisible(card.id)}
                            onChange={() => handleCardVisibilityToggle(card.id)}
                            color="primary"
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="subtitle1" fontWeight="medium">
                              {card.name}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {card.description}
                            </Typography>
                          </Box>
                        }
                      />
                    </Box>
                  ))}
                </FormGroup>

                <Box mt={3} p={2} bgcolor={theme.palette.action.hover} borderRadius={1}>
                  <Typography variant="body2" color="textSecondary">
                    <strong>Note:</strong> Pour réorganiser les cartes, faites-les glisser directement sur le tableau de bord.
                    L'ordre sera sauvegardé automatiquement.
                  </Typography>
                </Box>
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Annuler
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={loading || saving}
          startIcon={saving && <CircularProgress size={20} />}
        >
          {saving ? 'Enregistrement...' : 'Enregistrer'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default DashboardSettingsModal
